import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Pencil, Trash2, Plus, Sparkles, Save, X } from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import DateInput from '../components/DateInput';
import { useLanguage } from '../context/LanguageContext';

const EMPTY_FORM = { name_hd: '', date_hd: '', date_taken_hd: '' };
const currentYear = new Date().getFullYear();

export default function Holidays() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [generateYear, setGenerateYear] = useState(currentYear);
  const [generating, setGenerating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/holidays');
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('hol.fetchError') || 'Error loading holidays.' });
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setReason('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name_hd: row.name_hd || '',
      date_hd: row.date_hd ? row.date_hd.substring(0, 10) : '',
      date_taken_hd: (row.date_taken_hd || row.date_hd || '').substring(0, 10),
    });
    setReason('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (editing && !reason.trim()) {
      Swal.fire({ icon: 'warning', title: t('audit.reasonRequired') || 'Razon requerida', text: t('audit.reasonRequiredMsg') || 'Indica el motivo del cambio.' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/holidays/${editing.id_hd}`, { ...form, reason });
      } else {
        await axios.post('/api/holidays', form);
      }
      setModalOpen(false);
      fetchData();
      Swal.fire({ icon: 'success', title: editing ? t('hol.updated') : t('hol.created'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.saveError') });
    }
    setSaving(false);
  };

  const askDelete = async (id) => {
    const { value: reasonInput } = await Swal.fire({
      title: t('hol.deleteConfirm') || 'Eliminar holiday?',
      input: 'textarea',
      inputLabel: t('audit.reasonRequired') || 'Razon (obligatoria)',
      inputPlaceholder: t('audit.reasonPh') || 'Por que se esta eliminando?',
      inputValidator: (v) => !v?.trim() && (t('audit.reasonRequiredMsg') || 'La razon es obligatoria.'),
      showCancelButton: true,
      confirmButtonText: t('common.delete') || 'Eliminar',
      cancelButtonText: t('common.cancel') || 'Cancelar',
      confirmButtonColor: '#ef4444',
    });
    if (!reasonInput) return;
    setDeleteReason(reasonInput);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/holidays/${deleteId}`, { data: { reason: deleteReason } });
      setDeleteId(null);
      setDeleteReason('');
      fetchData();
      Swal.fire({ icon: 'success', title: t('hol.deleted'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.deleteError') });
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`/api/holidays/generate/${generateYear}`);
      fetchData();
      const holidayList = res.data.holidays.map(h => `${h.name}: ${h.date}`).join('<br>');
      Swal.fire({
        icon: 'success',
        title: `${t('hol.title')} ${generateYear}`,
        html: `<b>${res.data.inserted}</b> ${t('hol.generated')}${res.data.skipped.length ? `<br><b>${res.data.skipped.length}</b> ${t('hol.alreadyExist')}` : ''}<br><br><div style="text-align:left;font-size:13px">${holidayList}</div>`,
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('hol.generateError') });
    }
    setGenerating(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d.substring(0, 10) : d;
    const dt = new Date(s + (typeof s === 'string' && s.length === 10 ? 'T00:00:00' : ''));
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US');
  };

  const filteredData = data.filter(h => {
    if (!h.date_hd) return false;
    // Parse como fecha LOCAL (con T00:00:00) para evitar shift de zona horaria.
    // new Date('2026-01-01') se interpreta como UTC midnight y en UTC-6 se convierte a 2025-12-31.
    const s = typeof h.date_hd === 'string' ? h.date_hd.substring(0, 10) : h.date_hd;
    const d = new Date(s + (typeof s === 'string' && s.length === 10 ? 'T00:00:00' : ''));
    return d.getFullYear() === generateYear || (d.getMonth() === 0 && d.getDate() === 1 && d.getFullYear() === generateYear + 1);
  });

  const columns = [
    { header: t('common.name'), accessor: 'name_hd' },
    { header: t('hol.origDate'), render: (r) => formatDate(r.date_hd) },
    { header: t('hol.takenDate'), render: (r) => formatDate(r.date_taken_hd || r.date_hd) },
    {
      header: t('common.actions'),
      style: { width: 120 },
      render: (r) => (
        <div className="table-actions">
          <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}><Pencil size={14} style={{ marginRight: 4 }} />{t('common.edit')}</button>
          <button className="btn-danger btn-sm" onClick={() => askDelete(r.id_hd)}><Trash2 size={14} style={{ marginRight: 4 }} />{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('hol.title')}</h1>
        <div className="page-header-actions">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              className="form-input"
              style={{ width: 90, padding: '6px 8px', fontSize: 13 }}
              value={generateYear}
              onChange={e => setGenerateYear(Number(e.target.value))}
            >
              {Array.from({ length: 7 }, (_, i) => currentYear - 1 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              className="btn-secondary"
              style={{ width: 'auto', padding: '8px 16px', whiteSpace: 'nowrap' }}
              onClick={handleGenerate}
              disabled={generating}
            >
              <Sparkles size={15} style={{ marginRight: 6 }} />{generating ? t('hol.generating') : `${t('hol.generate')} ${generateYear}`}
            </button>
          </div>
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} onClick={openCreate}><Plus size={16} style={{ marginRight: 4 }} />{t('common.new')}</button>
        </div>
      </div>

      <div className="table-container">
        <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage={`${t('hol.empty')} ${generateYear}.`} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('hol.editTitle') : t('hol.newTitle')}>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t('hol.nameReq')}</label>
              <input className="form-input" required value={form.name_hd} onChange={e => setForm({ ...form, name_hd: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('hol.dateReq')}</label>
              <DateInput className="form-input" required value={form.date_hd} onChange={v => setForm(f => ({
                ...f,
                date_hd: v,
                // Auto-rellena la fecha a considerar si estaba vacia o igual a la fecha original anterior.
                date_taken_hd: (!f.date_taken_hd || f.date_taken_hd === f.date_hd) ? v : f.date_taken_hd,
              }))} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('hol.takenDate')}</label>
              <DateInput className="form-input" value={form.date_taken_hd} onChange={v => setForm({ ...form, date_taken_hd: v })} />
              <small style={{ color: 'var(--text-secondary)', fontSize: 12, display: 'block', marginTop: 4 }}>{t('hol.takenDateHelp')}</small>
            </div>
            {editing && (
              <div className="form-group">
                <label className="form-label">
                  {t('audit.reason') || 'Razon'} <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder={t('audit.reasonPh') || 'Por que se esta editando?'}
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} disabled={saving}>
              <Save size={15} style={{ marginRight: 4 }} />{saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message={t('hol.deleteConfirm')}
      />
    </div>
  );
}
